import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { formatDistanceToNow } from "date-fns";
import {
  Plus,
  Search,
  MoreVertical,
  Edit2,
  Trash2,
  Briefcase,
  Image as ImageIcon,
  Brain,
  Clock,
  LayoutGrid,
  List,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { motion, AnimatePresence } from "framer-motion";

export default function Inspectors() {
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState("grid"); // "grid" or "list"
  const [showForm, setShowForm] = useState(false);
  const [editingInspector, setEditingInspector] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    status: "active",
  });

  const queryClient = useQueryClient();

  const { data: inspectors = [], isLoading } = useQuery({
    queryKey: ["inspectors"],
    queryFn: async () => {
      // Get inspector profiles
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles_with_email")
        .select("*")
        .eq("role", "inspector")
        .order("created_at", { ascending: false });
      
      if (profilesError) throw profilesError;
      
      // Get photo counts and statistics for each inspector
      const inspectorsWithStats = await Promise.all(
        profiles.map(async (profile) => {
          // Get total photos
          const { count: totalPhotos, error: photosError } = await supabase
            .from("photos")
            .select("*", { count: "exact", head: true })
            .eq("inspector_id", profile.id);
          
          // Get analyzed photos count
          const { count: analyzedPhotos, error: analyzedError } = await supabase
            .from("photos")
            .select("*", { count: "exact", head: true })
            .eq("inspector_id", profile.id)
            .eq("analysis_status", "completed");
          
          // Get jobs count
          const { count: jobsCount, error: jobsError } = await supabase
            .from("jobs")
            .select("*", { count: "exact", head: true })
            .eq("inspector_id", profile.id);
          
          // Get recent photo
          const { data: recentPhoto, error: recentError } = await supabase
            .from("photos")
            .select("created_at")
            .eq("inspector_id", profile.id)
            .order("created_at", { ascending: false })
            .limit(1)
            .single();
          
          return {
            ...profile,
            name: profile.full_name,
            company: profile.organization_name,
            total_photos: totalPhotos || 0,
            analyzed_photos: analyzedPhotos || 0,
            jobs_count: jobsCount || 0,
            last_photo_at: recentPhoto?.created_at || null,
          };
        })
      );
      
      return inspectorsWithStats;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      // Create a new user via Supabase Auth (requires admin privileges or separate API)
      // For now, we'll just update an existing profile
      const { data: newInspector, error } = await supabase
        .from("profiles")
        .insert([{
          full_name: data.name,
          phone: data.phone,
          role: "inspector",
          status: data.status,
        }])
        .select()
        .single();
      if (error) throw error;
      return newInspector;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inspectors"] });
      closeForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const { data: updatedInspector, error } = await supabase
        .from("profiles")
        .update({
          full_name: data.name,
          phone: data.phone,
          status: data.status,
        })
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return updatedInspector;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inspectors"] });
      closeForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase
        .from("profiles")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inspectors"] });
    },
  });

  const closeForm = () => {
    setShowForm(false);
    setEditingInspector(null);
    setFormData({ name: "", email: "", phone: "", company: "", status: "active" });
  };

  const openEdit = (inspector) => {
    setEditingInspector(inspector);
    setFormData({
      name: inspector.full_name || inspector.name || "",
      email: inspector.email || "",
      phone: inspector.phone || "",
      company: inspector.company || "",
      status: inspector.status || "active",
    });
    setShowForm(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingInspector) {
      updateMutation.mutate({ id: editingInspector.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const filteredInspectors = inspectors.filter(
    (i) =>
      i.name?.toLowerCase().includes(search.toLowerCase()) ||
      i.email?.toLowerCase().includes(search.toLowerCase()) ||
      i.company?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Inspectors</h1>
          <p className="text-slate-500 mt-1">Manage inspector accounts and assignments</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="gap-2">
          <Plus className="w-4 h-4" />
          Add Inspector
        </Button>
      </div>

      {/* Search and View Toggle */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search inspectors..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        
        {/* View Toggle */}
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
          <Button
            variant={viewMode === "grid" ? "default" : "ghost"}
            size="sm"
            onClick={() => setViewMode("grid")}
            className="gap-2"
          >
            <LayoutGrid className="w-4 h-4" />
            <span className="hidden sm:inline">Grid</span>
          </Button>
          <Button
            variant={viewMode === "list" ? "default" : "ghost"}
            size="sm"
            onClick={() => setViewMode("list")}
            className="gap-2"
          >
            <List className="w-4 h-4" />
            <span className="hidden sm:inline">List</span>
          </Button>
        </div>
      </div>

      {/* Grid View */}
      {viewMode === "grid" && (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <AnimatePresence>
          {isLoading ? (
            Array(6)
              .fill(0)
              .map((_, i) => (
                <div key={i} className="bg-white rounded-2xl border border-slate-200 p-6 animate-pulse">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-slate-200 rounded-full" />
                    <div className="flex-1">
                      <div className="h-4 bg-slate-200 rounded w-3/4 mb-2" />
                      <div className="h-3 bg-slate-200 rounded w-1/2" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="h-3 bg-slate-200 rounded w-full" />
                    <div className="h-3 bg-slate-200 rounded w-2/3" />
                  </div>
                </div>
              ))
          ) : filteredInspectors.length === 0 ? (
            <div className="col-span-full text-center py-12 text-slate-500">
              No inspectors found
            </div>
          ) : (
            filteredInspectors.map((inspector) => (
              <motion.div
                key={inspector.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-lg hover:border-slate-300 transition-all duration-200 group relative"
              >
                {/* Header with Avatar and Actions */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold text-lg shadow-md">
                      {inspector.full_name?.[0] || inspector.name?.[0] || "?"}
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">
                        {inspector.full_name || inspector.name}
                      </h3>
                      <p className="text-sm text-slate-500">{inspector.email}</p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEdit(inspector)}>
                        <Edit2 className="w-4 h-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-rose-600"
                        onClick={() => deleteMutation.mutate(inspector.id)}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Status and Company */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <Badge
                    className={`border-0 ${
                      inspector.status === "active"
                        ? "bg-emerald-100 text-emerald-700"
                        : inspector.status === "inactive"
                        ? "bg-slate-100 text-slate-600"
                        : "bg-amber-100 text-amber-700"
                    }`}
                  >
                    {inspector.status || "pending"}
                  </Badge>
                  {inspector.company && (
                    <span className="text-sm text-slate-600">{inspector.company}</span>
                  )}
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-slate-600 mb-1">
                      <Briefcase className="w-4 h-4" />
                      <span className="text-xs font-medium">Jobs</span>
                    </div>
                    <p className="text-xl font-bold text-slate-900">{inspector.jobs_count || 0}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-slate-600 mb-1">
                      <ImageIcon className="w-4 h-4" />
                      <span className="text-xs font-medium">Photos</span>
                    </div>
                    <p className="text-xl font-bold text-slate-900">{inspector.total_photos || 0}</p>
                  </div>
                </div>

                {/* Analysis Progress */}
                {inspector.total_photos > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <div className="flex items-center gap-1 text-slate-600">
                        <Brain className="w-4 h-4 text-violet-500" />
                        <span className="font-medium">Analyzed</span>
                      </div>
                      <span className="font-semibold text-slate-900">
                        {inspector.analyzed_photos || 0} / {inspector.total_photos}
                      </span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-violet-500 to-violet-600 h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${Math.round(((inspector.analyzed_photos || 0) / inspector.total_photos) * 100)}%`,
                        }}
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1 text-right">
                      {Math.round(((inspector.analyzed_photos || 0) / inspector.total_photos) * 100)}% complete
                    </p>
                  </div>
                )}

                {/* Last Activity */}
                <div className="pt-4 border-t border-slate-100">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Clock className="w-4 h-4" />
                    <span>
                      {inspector.last_photo_at
                        ? formatDistanceToNow(new Date(inspector.last_photo_at), { addSuffix: true })
                        : "No recent activity"}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </AnimatePresence>
      </div>
      )}

      {/* List View */}
      {viewMode === "list" && (
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Inspector
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Jobs
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Photos
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Analyzed
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Last Activity
                </th>
                <th className="px-6 py-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <AnimatePresence>
                {isLoading ? (
                  Array(3)
                    .fill(0)
                    .map((_, i) => (
                      <tr key={i} className="animate-pulse">
                        <td className="px-6 py-4">
                          <div className="h-4 bg-slate-200 rounded w-32" />
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-4 bg-slate-200 rounded w-24" />
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-6 bg-slate-200 rounded w-16" />
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-4 bg-slate-200 rounded w-12" />
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-4 bg-slate-200 rounded w-12" />
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-4 bg-slate-200 rounded w-16" />
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-4 bg-slate-200 rounded w-24" />
                        </td>
                        <td className="px-6 py-4" />
                      </tr>
                    ))
                ) : filteredInspectors.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-slate-500">
                      No inspectors found
                    </td>
                  </tr>
                ) : (
                  filteredInspectors.map((inspector) => (
                    <motion.tr
                      key={inspector.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-medium">
                            {inspector.full_name?.[0] || inspector.name?.[0] || "?"}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{inspector.full_name || inspector.name}</p>
                            <p className="text-sm text-slate-500">{inspector.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-600">{inspector.company || "—"}</td>
                      <td className="px-6 py-4">
                        <Badge
                          className={`border-0 ${
                            inspector.status === "active"
                              ? "bg-emerald-100 text-emerald-700"
                              : inspector.status === "inactive"
                              ? "bg-slate-100 text-slate-600"
                              : "bg-amber-100 text-amber-700"
                          }`}
                        >
                          {inspector.status || "pending"}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-slate-700">
                          <Briefcase className="w-4 h-4 text-slate-400" />
                          <span className="font-medium">{inspector.jobs_count || 0}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-slate-700">
                          <ImageIcon className="w-4 h-4 text-slate-400" />
                          <span className="font-medium">{inspector.total_photos || 0}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {inspector.total_photos > 0 ? (
                          <div className="flex items-center gap-2">
                            <Brain className="w-4 h-4 text-violet-500" />
                            <span className="text-sm font-medium text-slate-700">
                              {inspector.analyzed_photos || 0}
                            </span>
                            <span className="text-xs text-slate-500">
                              ({Math.round(((inspector.analyzed_photos || 0) / inspector.total_photos) * 100)}%)
                            </span>
                          </div>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {inspector.last_photo_at ? (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Clock className="w-4 h-4 text-slate-400" />
                            <span>
                              {formatDistanceToNow(new Date(inspector.last_photo_at), { addSuffix: true })}
                            </span>
                          </div>
                        ) : (
                          <span className="text-slate-400">No activity</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEdit(inspector)}>
                              <Edit2 className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-rose-600"
                              onClick={() => deleteMutation.mutate(inspector.id)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </motion.tr>
                  ))
                )}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={closeForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingInspector ? "Edit Inspector" : "Add Inspector"}</DialogTitle>
          </DialogHeader>
          {!editingInspector && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <strong>Note:</strong> Creating new inspectors requires user authentication setup. 
              New users should be created through Supabase Auth or your admin panel.
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company">Company</Label>
              <Input
                id="company"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={closeForm} className="flex-1">
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1"
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {createMutation.isPending || updateMutation.isPending ? "Saving..." : "Save"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}